import { Component, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RoleFormatPipe } from '../../shared/pipes/role-format.pipe';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { UserService } from '../../core/services/user.service';
import { RoleService } from '../../core/services/role.service';
import { Role } from '../../core/models/models';

@Component({
  selector: 'app-user-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    RoleFormatPipe
  ],
  template: `
    <div class="page-header">
      <div class="title-section">
        <h2 class="page-title">Modifier l'utilisateur</h2>
        <p class="page-subtitle">Mettez à jour les informations du compte</p>
      </div>
      <a routerLink="/users" mat-stroked-button class="back-link">
        <mat-icon>arrow_back</mat-icon>
        Retour à la liste
      </a>
    </div>

    <div class="form-container">
      <div class="card shadow-sm">
        @if (isInitialLoading()) {
          <div class="loading-shade">
            <mat-icon class="spin">sync</mat-icon>
            Chargement...
          </div>
        } @else {
          <form [formGroup]="userForm" (ngSubmit)="onSubmit()" class="modern-form">
            <div class="form-section">
              <h3 class="section-title">Informations de connexion</h3>
              <div class="form-row">
                <mat-form-field appearance="outline" class="col">
                  <mat-label>NOM D'UTILISATEUR</mat-label>
                  <input matInput id="username" formControlName="username" placeholder="ex: jdupont">
                </mat-form-field>
                <mat-form-field appearance="outline" class="col">
                  <mat-label>ADRESSE EMAIL</mat-label>
                  <input matInput type="email" id="email" formControlName="email" placeholder="ex: jean.dupont&#64;avikeo.com">
                </mat-form-field>
              </div>
              <div class="form-row">
                <mat-form-field appearance="outline" class="col">
                  <mat-label>MOT DE PASSE (Laissez vide pour ne pas modifier)</mat-label>
                  <input matInput type="password" id="password" formControlName="password" placeholder="Minimum 6 caractères si renseigné">
                </mat-form-field>
                <mat-form-field appearance="outline" class="col">
                  <mat-label>RÔLE</mat-label>
                  <mat-select id="role" formControlName="role">
                    @for (role of roles(); track role.id) {
                      <mat-option [value]="role.name">{{ role | roleFormat }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
            </div>

            <div class="form-section">
              <h3 class="section-title">Informations personnelles</h3>
              <div class="form-row">
                <mat-form-field appearance="outline" class="col">
                  <mat-label>PRÉNOM</mat-label>
                  <input matInput id="firstName" formControlName="firstName" placeholder="ex: Jean">
                </mat-form-field>
                <mat-form-field appearance="outline" class="col">
                  <mat-label>NOM</mat-label>
                  <input matInput id="lastName" formControlName="lastName" placeholder="ex: Dupont">
                </mat-form-field>
              </div>
            </div>

            <div class="form-section">
              <mat-checkbox formControlName="accessEnabled" color="primary">
                Accès au compte activé
              </mat-checkbox>
            </div>

            @if (errorMessage()) {
              <div class="error-banner">
                <mat-icon>error</mat-icon>
                {{ errorMessage() }}
              </div>
            }

            <div class="form-actions">
              <button type="button" mat-button routerLink="/users">Annuler</button>
              <button type="submit" mat-raised-button color="primary" [disabled]="userForm.invalid || isLoading()">
                @if (isLoading()) { <mat-icon class="spin" matButtonIcon>sync</mat-icon> }
                @if (isLoading()) { <span>Mise à jour en cours...</span> } @else { <span>Enregistrer les modifications</span> }
              </button>
            </div>
          </form>
        }
      </div>
    </div>
  `,
  styles: [`
    .page-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 2rem; }
    .page-title { margin: 0; font-size: 1.5rem; color: #1a237e; }
    .page-subtitle { margin: 0.25rem 0 0; color: var(--text-secondary); font-size: 0.9rem; }
    .form-container { max-width: 800px; }
    .modern-form { padding: 1rem; }
    .form-section { margin-bottom: 2.5rem; }
    .section-title { font-size: 1rem; font-weight: 700; color: var(--primary-color); margin-bottom: 1.5rem; padding-bottom: 0.5rem; border-bottom: 2px solid #e8eaf6; }
    .form-row { display: flex; gap: 1.5rem; margin-bottom: 0.5rem; }
    .col { flex: 1; }
    .error-banner { background-color: #ffebee; color: #c62828; padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem; border-left: 4px solid var(--error-color); display: flex; align-items: center; gap: 0.5rem; }
    .form-actions { display: flex; justify-content: flex-end; gap: 1rem; padding-top: 1.5rem; border-top: 1px solid var(--border-color); }
    .loading-shade { display: flex; align-items: center; justify-content: center; padding: 4rem; gap: 1rem; color: #666; }
    .spin { animation: spin 1s linear infinite; }
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  `]
})
export class UserEditComponent implements OnInit {
  private fb = inject(FormBuilder);
  private userService = inject(UserService);
  private roleService = inject(RoleService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  userId: number | null = null;
  roles = signal<Role[]>([]);
  isInitialLoading = signal(true);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);

  userForm = this.fb.group({
    username: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    password: ['', [Validators.minLength(6)]],
    role: ['', [Validators.required]],
    accessEnabled: [true]
  });

  ngOnInit() {
    this.roleService.getAll().subscribe({
      next: (roles) => this.roles.set(roles)
    });

    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.userId = +idParam;
      this.loadUser(this.userId);
    } else {
      this.errorMessage.set('ID utilisateur manquant');
      this.isInitialLoading.set(false);
    }
  }

  loadUser(id: number) {
    this.userService.getUserById(id).subscribe({
      next: (user) => {
        this.userForm.patchValue({
          username: user.username,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          accessEnabled: user.accessEnabled,
          // Handle role if it's an object or string
          role: (user.role as any)?.name || user.role || ''
        });
        this.isInitialLoading.set(false);
      },
      error: (err) => {
        this.errorMessage.set('Impossible de charger l\'utilisateur');
        this.isInitialLoading.set(false);
      }
    });
  }

  onSubmit() {
    if (this.userForm.valid && this.userId) {
      this.isLoading.set(true);
      this.errorMessage.set(null);

      const formValue = this.userForm.value;

      // Map role to object as expected by back-end
      const userData: any = {
        username: formValue.username,
        email: formValue.email,
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        accessEnabled: formValue.accessEnabled,
        role: { name: formValue.role }
      };

      // Only include password if it's not empty
      if (formValue.password) {
        userData.password = formValue.password;
      }

      this.userService.updateUser(this.userId, userData).subscribe({
        next: () => {
          this.router.navigate(['/users']);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.errorMessage.set(err.error?.message || err.error || 'Une erreur est survenue lors de la mise à jour');
        }
      });
    }
  }
}
